import { useState, useEffect, useCallback } from "react";
import { Axios } from "../../../Api/axios";
import {
  ADMIN_ALL_EXCELLENT,
  ADMIN_ADD_EXCELLENT,
  ADMIN_REMOVE_EXCELLENT,
  ALLSTD,
  ADMIN_ALL_CLASSES,
  SUBJECTS_BY_CLASS,
  baseURL,
} from "../../../Api/Api";
import "../dash-cards.css";
import "./excellent.css";
import DashSelect from "../../../Components/Dashboard/DashSelect";

function formatAverage(raw) {
  if (raw == null) return null;
  if (typeof raw === "string" && raw.includes("/")) return raw;
  const num = parseFloat(raw);
  return isNaN(num) ? null : `${num}%`;
}

/* ─── Confirm Dialog ─── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="pick-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="confirm-dialog" dir="rtl">
        <div className="confirm-icon">
          <svg width="32" height="32" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        </div>
        <h3 className="confirm-title">إزالة التميز</h3>
        <p className="confirm-msg">
          هل تريد إزالة <strong>"{name}"</strong> من قائمة الطلاب المميزين؟
        </p>
        <div className="confirm-actions">
          <button className="confirm-cancel" onClick={onCancel}>إلغاء</button>
          <button className="confirm-ok" onClick={onConfirm}>نعم، إزالة</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Excellent student card ─── */
function ExcellentCard({ student, onRemove }) {
  const [removing, setRemoving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const photoUrl = student.photo
    ? `${baseURL}/uploads/students/${student.photo}`
    : null;
  const className = student.Class?.name || student.class?.name || null;

  async function doRemove() {
    setShowConfirm(false);
    setRemoving(true);
    try { await onRemove(student.id); }
    catch { setRemoving(false); }
  }

  return (
    <>
    {showConfirm && (
      <ConfirmDialog
        name={student.name}
        onConfirm={doRemove}
        onCancel={() => setShowConfirm(false)}
      />
    )}
    <div className="dc-card exc-card">
      <div className="dc-card-cap exc-cap" />
      <div className="dc-card-avatar" style={{ position: "relative" }}>
        {photoUrl ? (
          <img src={photoUrl} alt={student.name} />
        ) : (
          <div className="dc-card-avatar-placeholder dc-avatar-green">
            <svg width="30" height="30" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
          </div>
        )}
        <span className="exc-star-badge">★</span>
      </div>

      <div className="dc-card-body">
        <h3 className="dc-card-name">{student.name}</h3>
        {formatAverage(student.final_average) != null && (
          <p className="exc-average-badge">
            ★ {formatAverage(student.final_average)}
          </p>
        )}
        {student.year && (
          <p className="dc-card-meta dc-card-meta-green">
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
            </svg>
            {student.year}
          </p>
        )}
        {className && (
          <p className="dc-card-meta dc-meta-class">
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
            </svg>
            {className}
          </p>
        )}
        {student.excellent_subjects && (() => {
          let list = [];
          if (Array.isArray(student.excellent_subjects)) {
            list = student.excellent_subjects;
          } else if (typeof student.excellent_subjects === "string") {
            try {
              const p = JSON.parse(student.excellent_subjects);
              if (Array.isArray(p)) list = p;
            } catch {
              list = student.excellent_subjects
                .split(/[،,]/).filter(s => s.trim())
                .map(s => ({ name: s.trim(), grade: null }));
            }
          }
          return list.length > 0 ? (
            <div className="exc-subjects">
              {list.map((sub, i) => (
                <span key={i} className="exc-subject-tag">
                  {sub.name}
                  {sub.grade != null && (
                    <span className="exc-subject-grade">
                      {typeof sub.grade === "string" && sub.grade.includes("/") ? sub.grade : `${sub.grade}%`}
                    </span>
                  )}
                </span>
              ))}
            </div>
          ) : null;
        })()}
      </div>

      <div className="dc-card-actions">
        <button
          className="dc-action-btn dc-delete-btn"
          style={{ borderBottomRightRadius: 18, borderBottomLeftRadius: 18 }}
          onClick={() => setShowConfirm(true)}
          disabled={removing}
        >
          <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
          {removing ? "..." : "إزالة التميز"}
        </button>
      </div>
    </div>
    </>
  );
}

/* ─── Skeleton card ─── */
function SkeletonCard() {
  return (
    <div className="dc-card dc-skeleton">
      <div className="dc-card-cap" />
      <div className="dc-card-avatar" />
      <div className="dc-card-body" style={{ paddingBottom: 14 }}>
        <div className="dc-sk-line dc-sk-lg" />
        <div className="dc-sk-line dc-sk-sm" />
        <div className="dc-sk-line dc-sk-sm" />
      </div>
      <div className="dc-sk-actions">
        <div
          className="dc-sk-btn"
          style={{ borderBottomRightRadius: 18, borderBottomLeftRadius: 18 }}
        />
      </div>
    </div>
  );
}

/* ─── Pick-student dialog ─── */
function PickStudentDialog({ onClose, onPick, excellentIds }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [selected, setSelected]           = useState(null);
  const [finalAverageFull, setFinalAverageFull]         = useState("");
  const [finalAverageObtained, setFinalAverageObtained] = useState("");
  const [classSubjects, setClassSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [selectedSubs, setSelectedSubs]   = useState(new Set());
  const [subjectGrades, setSubjectGrades] = useState({});
  const [saving, setSaving]               = useState(false);

  useEffect(() => {
    Promise.all([
      Axios.get(`${ALLSTD}?limit=500&page=1`),
      Axios.get(ADMIN_ALL_CLASSES),
    ])
      .then(([stRes, clRes]) => {
        setStudents(stRes.data.students || []);
        setClasses(clRes.data.classes || clRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  /* Fetch class subjects when a student is picked */
  useEffect(() => {
    if (!selected) {
      setClassSubjects([]);
      setSelectedSubs(new Set());
      setSubjectGrades({});
      return;
    }
    const classId = selected.classId || selected.Class?.id;
    if (!classId) { setClassSubjects([]); return; }
    setSubjectsLoading(true);
    Axios.get(SUBJECTS_BY_CLASS(classId))
      .then((res) => setClassSubjects(res.data.subjects || []))
      .catch(() => setClassSubjects([]))
      .finally(() => setSubjectsLoading(false));
  }, [selected]);

  const filtered = students.filter((s) => {
    const matchSearch =
      !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.phone?.includes(search);
    const matchClass =
      !filterClass ||
      String(s.classId) === String(filterClass) ||
      String(s.Class?.id) === String(filterClass);
    return matchSearch && matchClass && !excellentIds.has(s.id);
  });

  function toggleSub(subId) {
    setSelectedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(subId)) {
        next.delete(subId);
        setSubjectGrades((g) => { const n = { ...g }; delete n[subId]; return n; });
      } else {
        next.add(subId);
      }
      return next;
    });
  }

  function setSubjectGradeField(subId, field, value) {
    setSubjectGrades((p) => ({
      ...p,
      [subId]: { ...p[subId], [field]: value },
    }));
  }

  const invalidSubjectIds = [...selectedSubs].filter((id) => {
    const g = subjectGrades[id];
    if (!g) return false;
    const full = g.full !== undefined && g.full !== "" ? parseFloat(g.full) : null;
    const obtained = g.obtained !== undefined && g.obtained !== "" ? parseFloat(g.obtained) : null;
    return full != null && obtained != null && full < obtained;
  });

  const finalAverageInvalid =
    finalAverageFull !== "" &&
    finalAverageObtained !== "" &&
    parseFloat(finalAverageFull) < parseFloat(finalAverageObtained);

  async function handleConfirm() {
    if (!selected || invalidSubjectIds.length > 0 || finalAverageInvalid) return;
    if (!finalAverageFull || !finalAverageObtained) return;
    const finalAverageStr = `${parseFloat(finalAverageFull)}/${parseFloat(finalAverageObtained)}`;
    const subjectsJson = classSubjects
      .filter((s) => selectedSubs.has(s.id))
      .map((s) => {
        const g = subjectGrades[s.id] || {};
        const full = g.full !== undefined && g.full !== "" ? parseFloat(g.full) : null;
        const obtained = g.obtained !== undefined && g.obtained !== "" ? parseFloat(g.obtained) : null;
        return {
          name:  s.name,
          grade: full != null && obtained != null ? `${full}/${obtained}` : null,
        };
      });
    setSaving(true);
    try { await onPick(selected, finalAverageStr, JSON.stringify(subjectsJson)); }
    catch { setSaving(false); }
  }

  return (
    <div
      className="pick-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pick-dialog" dir="rtl">
        {/* Header */}
        <div className="pick-header">
          <span>اختر طالباً لتمييزه</span>
          <button className="pick-close" onClick={onClose}>✕</button>
        </div>

        {/* Filters */}
        <div className="pick-filters">
          <div className="pick-search-wrap">
            <svg width="15" height="15" fill="#9ca3af" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              className="pick-search"
              type="text"
              placeholder="ابحث باسم الطالب أو رقمه..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <DashSelect
            value={filterClass}
            onChange={setFilterClass}
            options={[
              { value: "", label: "جميع الصفوف" },
              ...classes.map((c) => ({ value: c.id, label: c.name })),
            ]}
            icon={
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
              </svg>
            }
          />
        </div>

        {/* Student list + extra fields — scrollable together */}
        <div className="pick-scroll-area">
        <div className="pick-list">
          {loading ? (
            <div className="pick-loading">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="pick-empty">
              {search || filterClass
                ? "لا يوجد طلاب بهذه المعايير"
                : "جميع الطلاب مضافون بالفعل"}
            </div>
          ) : (
            filtered.map((s) => {
              const cn = s.Class?.name || s.class?.name || "";
              const isSelected = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  className={`pick-item${isSelected ? " pick-selected" : ""}`}
                  onClick={() => setSelected(s)}
                >
                  <div className="pick-item-avatar">
                    {s.photo ? (
                      <img
                        src={`${baseURL}/uploads/students/${s.photo}`}
                        alt={s.name}
                      />
                    ) : (
                      <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                      </svg>
                    )}
                  </div>
                  <div className="pick-item-info">
                    <span className="pick-item-name">{s.name}</span>
                    <span className="pick-item-meta">
                      {[cn, s.year].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  {isSelected && (
                    <svg
                      className="pick-check"
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>{/* end pick-list */}

        {/* Extra fields — shown only when a student is selected */}
        {selected && (
          <div className="pick-extra-fields">
            <div className="pick-field-group">
              <label className="pick-field-label">المعدل النهائي *</label>
              <div className="pick-avg-fields">
                <div className="pick-avg-col">
                  <span className="pick-avg-col-label">الحاصل عليه</span>
                  <input
                    className={`pick-field-input${finalAverageInvalid ? " pick-score-input-error" : ""}`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="مثال: 95.50"
                    value={finalAverageObtained}
                    onChange={(e) => setFinalAverageObtained(e.target.value)}
                  />
                </div>
                <span className="pick-avg-sep">/</span>
                <div className="pick-avg-col">
                  <span className="pick-avg-col-label">الكامل</span>
                  <input
                    className={`pick-field-input${finalAverageInvalid ? " pick-score-input-error" : ""}`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="مثال: 100"
                    value={finalAverageFull}
                    onChange={(e) => setFinalAverageFull(e.target.value)}
                  />
                </div>
              </div>
              {finalAverageInvalid && (
                <div className="pick-score-error-msg">
                  يجب أن يكون المعدل الكامل أكبر من أو يساوي المعدل الحاصل عليه
                </div>
              )}
            </div>
            <div className="pick-field-group">
              <label className="pick-field-label">
                المواد المتفوق فيها
                <span className="pick-field-optional">(اضغط لاختيار — اختياري)</span>
              </label>

              {subjectsLoading ? (
                <div className="pick-subjects-state">جاري تحميل المواد...</div>
              ) : classSubjects.length === 0 ? (
                <div className="pick-subjects-state pick-subjects-empty">
                  لا توجد مواد مسجّلة لهذا الصف
                </div>
              ) : (
                <>
                  {/* ── Chip grid ── */}
                  <div className="pick-chips-grid">
                    {classSubjects.map((sub) => {
                      const isSel = selectedSubs.has(sub.id);
                      return (
                        <button
                          key={sub.id}
                          type="button"
                          className={`pick-chip${isSel ? " pick-chip-active" : ""}`}
                          onClick={() => toggleSub(sub.id)}
                        >
                          {isSel && (
                            <svg width="11" height="11" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                            </svg>
                          )}
                          {sub.name}
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Score inputs for selected subjects ── */}
                  {selectedSubs.size > 0 && (
                    <div className="pick-scores-table">
                      <div className="pick-scores-header">
                        <span>المادة</span>
                        <span>الحاصل عليها</span>
                        <span>الكاملة</span>
                      </div>
                      {classSubjects
                        .filter((s) => selectedSubs.has(s.id))
                        .map((s) => {
                          const g = subjectGrades[s.id] || {};
                          const invalid = invalidSubjectIds.includes(s.id);
                          return (
                            <div key={s.id} className="pick-score-row">
                              <span className="pick-score-name">{s.name}</span>
                              <input
                                type="number"
                                className={`pick-score-input${invalid ? " pick-score-input-error" : ""}`}
                                placeholder="—"
                                min="0" step="0.5"
                                value={g.obtained ?? ""}
                                onChange={(e) => setSubjectGradeField(s.id, "obtained", e.target.value)}
                              />
                              <input
                                type="number"
                                className={`pick-score-input${invalid ? " pick-score-input-error" : ""}`}
                                placeholder="—"
                                min="0" step="0.5"
                                value={g.full ?? ""}
                                onChange={(e) => setSubjectGradeField(s.id, "full", e.target.value)}
                              />
                            </div>
                          );
                        })}
                      {invalidSubjectIds.length > 0 && (
                        <div className="pick-score-error-msg">
                          يجب أن تكون العلامة الكاملة أكبر من أو تساوي العلامة الحاصل عليها
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        </div>{/* end pick-scroll-area */}

        {/* Footer */}
        <div className="pick-footer">
          <button className="pick-cancel" onClick={onClose}>إلغاء</button>
          <button
            className="pick-confirm"
            onClick={handleConfirm}
            disabled={
              !selected ||
              !finalAverageFull ||
              !finalAverageObtained ||
              finalAverageInvalid ||
              saving ||
              invalidSubjectIds.length > 0
            }
          >
            {saving ? "جاري التمييز..." : "★ تمييز الطالب"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ─── */
export default function ExcellentStudents() {
  const [excellent, setExcellent] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  const fetchExcellent = useCallback(() => {
    setLoading(true);
    Axios.get(ADMIN_ALL_EXCELLENT)
      .then((res) => setExcellent(res.data.students || res.data || []))
      .catch(() => setExcellent([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchExcellent(); }, [fetchExcellent]);

  async function handleRemove(studentId) {
    await Axios.delete(ADMIN_REMOVE_EXCELLENT(studentId));
    setExcellent((prev) => prev.filter((s) => s.id !== studentId));
  }

  async function handlePick(student, finalAverage, subjects) {
    const res = await Axios.post(ADMIN_ADD_EXCELLENT, {
      studentId:          student.id,
      final_average:      finalAverage || null,
      excellent_subjects: subjects     || null,
    });
    const saved = res.data.student || { ...student, final_average: finalAverage, excellent_subjects: subjects };
    setExcellent((prev) => [...prev, saved]);
    setShowPicker(false);
  }

  const excellentIds = new Set(excellent.map((s) => s.id));

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">
            <span className="exc-title-star">★</span>
            الطلاب المتميزون
          </h1>
          <p className="exc-subtitle">
            الطلاب المختارون للظهور في قسم الأوائل على الصفحة الرئيسية
          </p>
        </div>
        <button
          className="dash-add-btn exc-add-btn"
          onClick={() => setShowPicker(true)}
        >
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          تمييز طالب
        </button>
      </div>

      <div className="exc-count-badge">
        ★ {loading ? "..." : `${excellent.length} طالب مميز`}
      </div>

      <div className="card-grid">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
        ) : excellent.length === 0 ? (
          <div className="dc-empty">
            <svg width="56" height="56" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <p>لا يوجد طلاب مميزون بعد</p>
            <small>اضغط "تمييز طالب" لإضافة أوائل الطلاب</small>
          </div>
        ) : (
          excellent.map((student) => (
            <ExcellentCard
              key={student.id}
              student={student}
              onRemove={handleRemove}
            />
          ))
        )}
      </div>

      {showPicker && (
        <PickStudentDialog
          onClose={() => setShowPicker(false)}
          onPick={handlePick}
          excellentIds={excellentIds}
        />
      )}
    </div>
  );
}
